import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const SponsorCylinder = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [settings, setSettings] = useState({ speed: 80, enabled: true });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipAngle, setFlipAngle] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [transitionOn, setTransitionOn] = useState(true);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [spRes, stRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/sponsors`),
          fetch(`${BACKEND_URL}/api/sponsors/settings`),
        ]);
        if (spRes.ok) setImages(await spRes.json());
        if (stRes.ok) setSettings(await stRes.json());
      } catch {}
    };
    fetchData();
  }, [BACKEND_URL]);

  const count = images.length;

  // Flip forward (left) — page turns like a book
  const flipForward = useCallback(() => {
    if (animating || count === 0) return;
    setAnimating(true);
    setTransitionOn(true);
    setFlipAngle(-180); // flip left

    setTimeout(() => {
      // Snap reset: disable transition, set angle to 0, update index
      setTransitionOn(false);
      setFlipAngle(0);
      setCurrentIndex(prev => (prev + 1) % count);
      // Re-enable transition after browser paints the reset
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionOn(true);
          setAnimating(false);
        });
      });
    }, 650);
  }, [animating, count]);

  // Flip backward (right) — only on manual "Prev"
  const flipBackward = useCallback(() => {
    if (animating || count === 0) return;
    setAnimating(true);
    setTransitionOn(true);
    setFlipAngle(180); // flip right

    setTimeout(() => {
      setTransitionOn(false);
      setFlipAngle(0);
      setCurrentIndex(prev => (prev - 1 + count) % count);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionOn(true);
          setAnimating(false);
        });
      });
    }, 650);
  }, [animating, count]);

  // Auto-advance every 4 seconds, always forward
  useEffect(() => {
    if (paused || count === 0) return;
    timerRef.current = setInterval(flipForward, 4000);
    return () => clearInterval(timerRef.current);
  }, [paused, count, flipForward]);

  if (count === 0 || !settings.enabled) return null;

  const current = images[currentIndex];
  // The "under" page — what's revealed when the current flips away
  const nextPage = images[(currentIndex + 1) % count];
  const prevPage = images[(currentIndex - 1 + count) % count];
  // Show the next or prev page underneath depending on flip direction
  const underPage = flipAngle <= 0 ? nextPage : prevPage;
  const pageNum = currentIndex + 1;

  const handleClick = () => {
    navigate('/elevated-escape/tickets#tickets');
  };

  return (
    <section
      className="py-16 overflow-hidden relative"
      style={{ background: 'linear-gradient(180deg, #030012 0%, #0a0a1a 50%, #030012 100%)' }}
      data-testid="sponsor-cylinder-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #d4a843 0%, transparent 70%)' }} />
      </div>

      <div className="mx-auto max-w-5xl px-6 lg:px-8 relative z-10">
        <p className="text-brand-gold text-xs uppercase tracking-[0.25em] text-center mb-2">Field Guide</p>
        <h2 className="text-2xl sm:text-3xl font-semibold text-white text-center mb-2">Sponsors, Artists & Contributors</h2>
        <p className="text-gray-500 text-sm text-center mb-10">Click any page to get tickets</p>
      </div>

      {/* Flipbook */}
      <div className="flex justify-center items-center relative z-10" style={{ perspective: '1200px' }}>
        <div
          className="relative cursor-pointer"
          onClick={handleClick}
          style={{ width: '280px', height: '340px' }}
          data-testid="sponsor-cylinder"
        >
          {/* Book spine shadow */}
          <div className="absolute -left-1 top-2 bottom-2 w-3 rounded-l-sm" style={{ background: 'linear-gradient(90deg, rgba(139,90,43,0.6) 0%, rgba(90,60,30,0.3) 100%)', boxShadow: '-2px 0 8px rgba(0,0,0,0.5)' }} />

          {/* Stacked pages behind (decorative depth) */}
          {[3, 2, 1].map(offset => (
            <div
              key={offset}
              className="absolute rounded-r-sm"
              style={{
                width: '280px',
                height: '340px',
                right: `${-offset * 2}px`,
                top: `${offset}px`,
                background: `hsl(35, ${20 - offset * 3}%, ${85 + offset * 3}%)`,
                border: '1px solid rgba(139,90,43,0.15)',
                boxShadow: `${offset}px ${offset}px ${offset * 2}px rgba(0,0,0,0.1)`,
              }}
            />
          ))}

          {/* Under page (revealed when current flips away) */}
          <div
            className="absolute inset-0 rounded-r-sm overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #f5f0e8 0%, #ede4d4 100%)',
              border: '1px solid rgba(139,90,43,0.25)',
              zIndex: 1,
            }}
          >
            <div className="w-full h-full p-4 flex flex-col items-center justify-center">
              <p className="text-[#8b5a2b] text-[9px] uppercase tracking-[0.2em] font-semibold mb-2">Elevated Escape Camping Guide</p>
              <div className="w-full flex-1 flex items-center justify-center overflow-hidden">
                <img
                  src={underPage?.src}
                  alt={underPage?.alt || ''}
                  className="max-w-full max-h-[260px] object-contain drop-shadow-md"
                  draggable={false}
                />
              </div>
            </div>
          </div>

          {/* Current page — flips left (forward) or right (backward) */}
          <div
            className="absolute inset-0 rounded-r-sm overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #f5f0e8 0%, #ede4d4 100%)',
              border: '1px solid rgba(139,90,43,0.25)',
              boxShadow: animating
                ? '8px 4px 20px rgba(0,0,0,0.4), inset -2px 0 6px rgba(139,90,43,0.08)'
                : '4px 4px 15px rgba(0,0,0,0.3), inset -2px 0 6px rgba(139,90,43,0.08)',
              transformOrigin: 'left center',
              transform: `rotateY(${flipAngle}deg)`,
              transition: transitionOn ? 'transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1), box-shadow 0.6s ease' : 'none',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              zIndex: 2,
            }}
            data-testid={`sponsor-card-${currentIndex}`}
          >
            {/* Page texture */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'6\' height=\'6\' viewBox=\'0 0 6 6\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23000\' fill-opacity=\'1\'%3E%3Cpath d=\'M5 0h1L0 6V5zM6 5v1H5z\'/%3E%3C/g%3E%3C/svg%3E")' }} />

            <div className="w-full h-full p-4 flex flex-col items-center justify-center relative">
              {/* Corner fold */}
              <div className="absolute top-0 right-0 w-8 h-8 overflow-hidden">
                <div className="absolute -top-4 -right-4 w-8 h-8 rotate-45" style={{ background: 'linear-gradient(135deg, transparent 50%, rgba(139,90,43,0.12) 50%)' }} />
              </div>

              <p className="text-[#8b5a2b] text-[9px] uppercase tracking-[0.2em] font-semibold mb-2">Elevated Escape Camping Guide</p>

              <div className="w-full flex-1 flex items-center justify-center overflow-hidden">
                <img
                  src={current?.src}
                  alt={current?.alt || ''}
                  className="max-w-full max-h-[260px] object-contain drop-shadow-md"
                  draggable={false}
                />
              </div>

              {/* Page number */}
              <div className="absolute bottom-2 right-3">
                <span className="text-[10px] text-[#8b5a2b]/40 font-mono">{pageNum} / {count}</span>
              </div>
            </div>
          </div>

          {/* Pause hint */}
          {paused && !animating && (
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-gray-500 text-[10px] tracking-wider uppercase animate-pulse">
              paused
            </div>
          )}
        </div>
      </div>

      {/* Page indicator dots */}
      <div className="flex justify-center gap-1 mt-8 relative z-10">
        {Array.from({ length: Math.min(count, 20) }).map((_, i) => (
          <div
            key={i}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
              i === currentIndex % Math.min(count, 20) ? 'bg-brand-gold scale-125' : 'bg-white/15'
            }`}
          />
        ))}
        {count > 20 && <span className="text-white/20 text-[10px] ml-1">+{count - 20}</span>}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-center gap-4 mt-4 relative z-10">
        <button
          onClick={(e) => { e.stopPropagation(); flipBackward(); }}
          className="text-gray-500 hover:text-brand-gold text-xs uppercase tracking-wider transition-colors flex items-center gap-1"
          data-testid="carousel-prev"
        >
          <span className="text-lg leading-none">&lsaquo;</span> Prev
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); flipForward(); }}
          className="text-gray-500 hover:text-brand-gold text-xs uppercase tracking-wider transition-colors flex items-center gap-1"
          data-testid="carousel-next"
        >
          Next <span className="text-lg leading-none">&rsaquo;</span>
        </button>
      </div>
    </section>
  );
};

export default SponsorCylinder;
